import { format } from 'date-fns';
import { OrderType } from '../types/orders';

/**
 * Nombrado ROID de imágenes — módulo PURO, sin dependencias de red.
 *
 * Vivía dentro de `utils/image.ts`, que importa `utils/sketch.ts` y con él el
 * cliente Apollo entero. Cualquier módulo que solo necesitara generar un nombre
 * se traía el cliente GraphQL detrás (y era imposible testearlo aislado).
 *
 * `utils/image.ts` lo reexporta, así que los imports existentes siguen
 * funcionando sin tocarlos.
 */

export type ImageType = 'photo' | 'canvas';

export interface OrderMetadata {
  indexExpedition: number;
  indexType: number;
  classification: string; // '01', '02', '03'
  orderType: OrderType;
}

export interface GenerateImageNameParams extends OrderMetadata {
  imageType: ImageType;
  sequence?: number;
}

/**
 * Prefijo del ROID según el tipo de orden (`At`, `Ac`, `Co`, `Tr` u `Ot`).
 *
 * El tramo del recorrido es entidad doble con Order y su ROID es
 * `Tr.<indexExpedition>.<indexType>.02` (ver `types/orders.ts`). Sin este caso
 * caía en 'Ot' y la foto quedaba nombrada como si fuera de una OT genérica — el
 * nombre viaja al ZIP de descarga.
 */
export const roidTypeCode = (orderType: OrderType | undefined): string =>
  orderType === OrderType.Action
    ? 'At'
    : orderType === OrderType.Accident
      ? 'Ac'
      : orderType === OrderType.Communication
        ? 'Co'
        : orderType === OrderType.RouteSegment
          ? 'Tr'
          : 'Ot';

/**
 * ROID legible de una orden a partir de los metadatos que viajan con cada
 * entrada de la bandeja de fotos: `At.0003016.0045.02`.
 *
 * Tolera metadatos incompletos (entradas antiguas): pone lo que haya en vez de
 * reventar, porque se usa para avisar al operario de una foto perdida y ese
 * aviso no puede fallar (spec 2026-10-01-photo-outbox-identity § 5.4).
 */
export const formatRoid = (meta: Partial<OrderMetadata>): string =>
  [
    roidTypeCode(meta.orderType),
    meta.indexExpedition === undefined
      ? undefined
      : String(meta.indexExpedition).padStart(7, '0'),
    meta.indexType === undefined ? undefined : String(meta.indexType).padStart(4, '0'),
    meta.classification || undefined,
  ]
    .filter((part): part is string => part !== undefined)
    .join('.');

/**
 * Genera el nombre estándar de imagen con patrón ROID.
 * Formato: `At_0000074_0006_01_20251031_1430_photo_01`
 */
export const generateImageNameWithROID = ({
  orderType,
  indexExpedition,
  indexType,
  classification,
  imageType,
  sequence,
}: GenerateImageNameParams): string => {
  const typeCode = roidTypeCode(orderType);

  const formattedExpedition = indexExpedition.toString().padStart(7, '0');
  const formattedType = indexType.toString().padStart(4, '0');

  const now = new Date();
  const dateStr = format(now, 'yyyyMMdd');
  const timeStr = format(now, 'HHmm');

  const parts = [
    typeCode,
    formattedExpedition,
    formattedType,
    classification,
    dateStr,
    timeStr,
    imageType,
  ];

  if (sequence !== undefined) {
    parts.push(sequence.toString().padStart(2, '0'));
  }

  return parts.join('_');
};
